import type { ButtonProps as ChakraButtonProps } from '@chakra-ui/react';
import { AbsoluteCenter, Button as ChakraButton, Span } from '@chakra-ui/react';
import React, { forwardRef } from 'react';

import Spinner from 'ui/Spinner';

type ButtonSize = 'xs' | 'sm' | 'md' | 'lg';

type ButtonVariant =
  | 'primary-solid'
  | 'primary-outline'
  | 'primary-ghost'
  | 'neutral-solid'
  | 'neutral-ghost'
  | 'danger-solid'
  | 'danger-ghost';

export type ButtonProps = Omit<ChakraButtonProps, 'size' | 'variant'> & {
  size?: ButtonSize;
  variant: ButtonVariant;
  loading?: boolean;
  loadingText?: React.ReactNode;
};

const buttonForwardRef = forwardRef<
  HTMLButtonElement,
  Omit<ButtonProps, 'variant'>
>;

const buttonMap: Record<
  ButtonVariant,
  React.ForwardRefExoticComponent<
    Omit<ButtonProps, 'variant'> & React.RefAttributes<HTMLButtonElement>
  >
> = {
  'primary-solid': buttonForwardRef((props, ref) => (
    <ChakraButton
      ref={ref}
      variant="solid"
      background="green.800"
      color="white"
      _active={{ background: 'green.700' }}
      _hover={{ background: 'green.700' }}
      _disabled={{
        background: 'gray.200',
        color: 'gray.500',
        opacity: 1,
      }}
      {...props}
    />
  )),
  'primary-outline': buttonForwardRef((props, ref) => (
    <ChakraButton
      ref={ref}
      variant="outline"
      background="red.50"
      border="2px solid"
      borderColor="green.800"
      color="green.800"
      _active={{ background: 'red.100' }}
      _hover={{ background: 'red.100' }}
      _disabled={{
        background: 'gray.100',
        borderColor: 'gray.300',
        color: 'gray.400',
        opacity: 1,
      }}
      {...props}
    />
  )),
  'primary-ghost': buttonForwardRef((props, ref) => (
    <ChakraButton
      ref={ref}
      variant="ghost"
      background="green.50"
      color="green.700"
      _active={{ background: 'green.100', color: 'green.800' }}
      _hover={{ background: 'green.100', color: 'green.800' }}
      _disabled={{
        background: 'gray.100',
        color: 'gray.400',
        opacity: 1,
      }}
      {...props}
    />
  )),
  'neutral-solid': buttonForwardRef((props, ref) => (
    <ChakraButton
      ref={ref}
      variant="solid"
      background="gray.700"
      color="white"
      _active={{ background: 'gray.600' }}
      _hover={{ background: 'gray.600' }}
      _disabled={{
        background: 'gray.200',
        color: 'gray.500',
        opacity: 1,
      }}
      {...props}
    />
  )),
  'neutral-ghost': buttonForwardRef((props, ref) => (
    <ChakraButton
      ref={ref}
      variant="ghost"
      bg="gray.100"
      color="gray.700"
      _active={{ background: 'gray.200', color: 'gray.800' }}
      _hover={{ background: 'gray.200', color: 'gray.800' }}
      _disabled={{
        background: 'gray.100',
        color: 'gray.400',
        opacity: 1,
      }}
      {...props}
    />
  )),
  'danger-solid': buttonForwardRef((props, ref) => (
    <ChakraButton
      ref={ref}
      variant="solid"
      background="red.600"
      color="white"
      _active={{ background: 'red.700' }}
      _hover={{ background: 'red.700' }}
      _disabled={{
        background: 'gray.200',
        color: 'gray.500',
        opacity: 1,
      }}
      {...props}
    />
  )),
  'danger-ghost': buttonForwardRef((props, ref) => (
    <ChakraButton
      ref={ref}
      variant="ghost"
      background="red.50"
      color="red.600"
      _active={{ background: 'red.100', color: 'red.700' }}
      _hover={{ background: 'red.100', color: 'red.700' }}
      _disabled={{
        background: 'gray.100',
        color: 'gray.400',
        opacity: 1,
      }}
      {...props}
    />
  )),
};

const sizeProps: Record<ButtonSize, Partial<ButtonProps>> = {
  xs: { px: 1, h: 6, fontSize: 'xs', borderRadius: 'md' },
  sm: { px: 3, h: 8, fontSize: 'sm', borderRadius: 'lg' },
  md: { px: 4, h: 10, fontSize: 'md', borderRadius: 'lg' },
  lg: { px: 5, h: 12, fontSize: 'lg', borderRadius: 'xl' },
};

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { disabled, loading, loadingText, size = 'md', variant, children, ...rest },
    ref
  ) => {
    const ButtonComponent = buttonMap[variant];
    return (
      <ButtonComponent
        ref={ref}
        disabled={loading || disabled}
        focusRingOffset="0"
        focusVisibleRing="mixed" // Use focusVisibleRing to avoid focus ring on click
        fontWeight="medium"
        minWidth="auto"
        size={size}
        _focus={{
          focusRingColor: 'blue.500', // Not sure why this needs to be nested in _focus to work but that's the case
          zIndex: 1, // Float focused buttons so that the focus ring is fully visible
        }}
        {...sizeProps[size]}
        {...rest}
      >
        {loading && !loadingText ? (
          <>
            <AbsoluteCenter display="inline-flex">
              <Spinner size="inherit" color="inherit" />
            </AbsoluteCenter>
            <Span opacity={0}>{children}</Span>
          </>
        ) : loading && loadingText ? (
          <>
            <Spinner size="inherit" color="inherit" />
            {loadingText}
          </>
        ) : (
          children
        )}
      </ButtonComponent>
    );
  }
);

export default Button;
